import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Form, Input, message } from 'antd';
import { 
  CreditCard, 
  Cloud, 
  Globe, 
  Wallet, 
  CheckCircle, 
  AlertCircle,
  ArrowRight,
  ShieldCheck,
  ExternalLink
} from 'lucide-react';
import api from '../api/client';
import clsx from 'clsx';

const IntegrationCard = ({ icon: Icon, title, description, benefits, isActive, onConnect, colorClass }) => (
  <motion.div 
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className={clsx(
      "glass-panel p-6 flex flex-col h-full relative overflow-hidden group border transition-all duration-300",
      isActive ? "border-emerald-500/50 shadow-[0_0_20px_rgba(16,185,129,0.2)]" : "border-white/5 hover:border-primary/50"
    )}
  >
    {isActive && (
      <div className="absolute top-4 right-4 flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/20 text-emerald-400 text-xs font-medium border border-emerald-500/20">
        <CheckCircle size={12} />
        Подключено
      </div>
    )}

    <div className={clsx("w-14 h-14 rounded-2xl flex items-center justify-center mb-6 text-white shadow-lg", colorClass)}>
      <Icon size={32} />
    </div>

    <h3 className="text-xl font-bold text-white mb-2">{title}</h3>
    <p className="text-slate-400 text-sm mb-6 flex-1">{description}</p>

    <div className="space-y-3 mb-8">
      {benefits.map((benefit, i) => (
        <div key={i} className="flex items-center gap-2 text-sm text-slate-300">
          <CheckCircle size={14} className="text-primary" />
          {benefit}
        </div>
      ))}
    </div>

    <button 
      onClick={onConnect}
      className={clsx(
        "w-full py-3 rounded-xl font-medium transition-all flex items-center justify-center gap-2 group-hover:gap-3",
        isActive 
          ? "bg-white/5 text-white hover:bg-white/10"
          : "bg-white/5 text-white hover:bg-gradient-to-r hover:from-primary hover:to-secondary"
      )}
    >
      {isActive ? 'Настроить' : 'Подключить'}
      <ArrowRight size={16} />
    </button>
  </motion.div>
);

export default function Integrations() {
  const [form] = Form.useForm();
  const [activeIntegration, setActiveIntegration] = useState(null); // 'prodamus', 'cloudpayments', etc.
  const [loading, setLoading] = useState(false);

  const handleSaveIntegration = async (values) => {
    setLoading(true);
    try {
      await api.post('/integrations', {
        integration_type: activeIntegration,
        credentials: values,
      });
      message.success('Интеграция успешно сохранена!');
      setActiveIntegration(null);
      form.resetFields();
    } catch (err) {
      message.error('Ошибка при сохранении');
    } finally {
      setLoading(false);
    }
  };

  const renderFormFields = () => {
    switch(activeIntegration) {
      case 'prodamus':
        return (
          <>
            <Form.Item 
              label={<span className="text-slate-300">API Ключ</span>}
              name="apiKey" 
              rules={[{ required: true, message: 'Введите API ключ' }]}
            >
              <Input.Password className="input-field" placeholder="Ваш API ключ Prodamus" />
            </Form.Item>
            <Form.Item 
              label={<span className="text-slate-300">API URL</span>}
              name="apiUrl" 
              initialValue="https://api.prodamus.ru"
            >
              <Input className="input-field" placeholder="https://api.prodamus.ru" />
            </Form.Item>
          </>
        );
      case 'cloudpayments':
        return (
          <>
            <Form.Item 
              label={<span className="text-slate-300">Public ID</span>}
              name="publicId" 
              rules={[{ required: true }]}
            >
              <Input className="input-field" placeholder="Ваш Public ID" />
            </Form.Item>
            <Form.Item 
              label={<span className="text-slate-300">API Secret</span>}
              name="apiSecret" 
              rules={[{ required: true }]}
            >
              <Input.Password className="input-field" placeholder="Ваш API Secret" />
            </Form.Item>
          </>
        );
      case 'stripe':
        return (
          <>
            <Form.Item 
              label={<span className="text-slate-300">Secret Key</span>}
              name="secretKey" 
              rules={[{ required: true }]}
            >
              <Input.Password className="input-field" placeholder="sk_test_..." />
            </Form.Item>
            <Form.Item 
              label={<span className="text-slate-300">Publishable Key</span>}
              name="publishableKey" 
              rules={[{ required: true }]}
            >
              <Input className="input-field" placeholder="pk_test_..." />
            </Form.Item>
          </>
        );
      case 'tribute':
        return (
          <>
            <Form.Item 
              label={<span className="text-slate-300">API Key</span>}
              name="apiKey" 
              rules={[{ required: true }]}
            >
              <Input.Password className="input-field" placeholder="Ваш Tribute API Key" />
            </Form.Item>
            <Form.Item 
              label={<span className="text-slate-300">Account ID</span>}
              name="accountId" 
              rules={[{ required: true }]}
            >
              <Input className="input-field" placeholder="Ваш Account ID" />
            </Form.Item>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-12">
        <h1 className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-white to-slate-400 mb-4">
          Платежные интеграции
        </h1>
        <div className="bg-primary/10 border border-primary/20 rounded-xl p-6 flex items-start gap-4 max-w-3xl">
          <ShieldCheck className="text-primary flex-shrink-0 mt-1" size={24} />
          <div>
            <h3 className="text-white font-medium mb-2">Безопасное подключение</h3>
            <p className="text-slate-400 text-sm leading-relaxed">
              Мы используем ваши API ключи только для получения статистики о платежах. 
              Все данные шифруются и хранятся в защищенном виде. Платежи проходят напрямую через платежную систему.
            </p>
          </div>
        </div>
      </div>

      {activeIntegration ? (
        <motion.div 
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className="max-w-2xl"
        >
          <div className="glass-panel p-8">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-2xl font-bold text-white capitalize flex items-center gap-3">
                <span className="text-slate-400 font-normal">Настройка:</span>
                {activeIntegration}
              </h2>
              <button 
                onClick={() => setActiveIntegration(null)}
                className="text-slate-400 hover:text-white transition-colors"
              >
                Назад
              </button>
            </div>

            <Form
              form={form}
              layout="vertical"
              onFinish={handleSaveIntegration}
              className="space-y-6"
            >
              {renderFormFields()}

              <div className="pt-6 flex gap-4">
                <button 
                  type="button"
                  onClick={() => setActiveIntegration(null)}
                  className="px-6 py-3 rounded-xl border border-white/10 text-white hover:bg-white/5 transition-colors"
                >
                  Отмена
                </button>
                <button 
                  type="submit"
                  disabled={loading}
                  className="flex-1 btn-primary flex items-center justify-center gap-2"
                >
                  {loading ? 'Сохранение...' : 'Сохранить интеграцию'}
                </button>
              </div>
            </Form>

            <div className="mt-8 pt-8 border-t border-white/5">
              <a 
                href="#" 
                className="flex items-center gap-2 text-sm text-slate-400 hover:text-primary transition-colors"
              >
                <ExternalLink size={14} />
                Инструкция по получению ключей
              </a>
            </div>
          </div>
        </motion.div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <IntegrationCard 
            icon={CreditCard}
            title="Prodamus"
            description="Основное решение для приема платежей в РФ. Поддержка карт, СБП и рассрочек."
            benefits={['Низкие комиссии', 'Быстрые выплаты', 'Чеки (54-ФЗ)']}
            colorClass="bg-gradient-to-br from-purple-500 to-indigo-600"
            onConnect={() => setActiveIntegration('prodamus')}
          />
          
          <IntegrationCard 
            icon={Cloud}
            title="CloudPayments"
            description="Надежный эквайринг для интернет-магазинов и онлайн-сервисов."
            benefits={['Рекуррентные платежи', 'Apple/Google Pay', 'Высокая конверсия']}
            colorClass="bg-gradient-to-br from-blue-500 to-cyan-500"
            onConnect={() => setActiveIntegration('cloudpayments')}
          />

          <IntegrationCard 
            icon={Globe}
            title="Stripe"
            description="Международные платежи. Принимайте оплату со всего мира в USD/EUR."
            benefits={['Глобальный охват', 'Мультивалютность', 'Удобное API']}
            colorClass="bg-gradient-to-br from-indigo-500 to-violet-600"
            onConnect={() => setActiveIntegration('stripe')}
          />

          <IntegrationCard 
            icon={Wallet}
            title="Tribute"
            description="Современная платформа монетизации для Telegram-каналов и ботов."
            benefits={['Интеграция с Telegram', 'Донаты и подписки', 'Удобный вывод']}
            colorClass="bg-gradient-to-br from-pink-500 to-rose-500"
            onConnect={() => setActiveIntegration('tribute')}
          />
        </div>
      )}
    </div>
  );
}
